<template>
  <VaCard class="flex-nowrap">
    <VaCardTitle class="flex justify-between">
      <h3 class="mb-3 font-bold text-lg" style="text-transform: none">Deshabilitar armador</h3>
    </VaCardTitle>
    <VaCardContent class="overflow-x-auto">
      <div class="flex flex-col gap-2">
        <div class="body-content">
          <div class="scrollable-data-list">
            <VaDataTable
              v-if="showSearch"
              class="region-revenue-table"
              :columns="[
                { key: 'nombre', label: 'Nombre' },
                { key: 'rut', label: 'RUT', align: 'left' },
                { key: 'activeViajes', label: 'Viajes Activos', align: 'center' },
                { key: 'embarcaciones', label: 'Embarcaciones' },
              ]"
              :items="showList"
              selectable
              select-mode="multiple"
              selected-color="primary"
              @selectionChange="selectedItemsEmitted = $event.currentSelectedItems"
            >
              <template #cell(nombre)="{ rowData }">
                {{ rowData.tipo_persona === 'juridica' ? rowData.razon_social : rowData.nombre_representante }}
              </template>
              <template #cell(rut)="{ rowData }">
                {{ rowData.tipo_persona === 'juridica' ? rowData.rut_empresa : rowData.rut_representante }}
              </template>
              <template #cell(activeViajes)="{ rowData }">
                {{ rowData.activeViajes.length }}
              </template>
              <template #cell(embarcaciones)="{ rowData }">
                <div class="flex flex-col gap-1">
                  <div
                    v-for="viaje in rowData.activeViajes.reduce((uniqueEmb, viaje) => {
                      if (!uniqueEmb.some((v) => v.embarcacionData.id === viaje.embarcacionData.id)) {
                        uniqueEmb.push(viaje)
                      }
                      return uniqueEmb
                    }, [] as ViajeEmbarcacion[])"
                    :key="viaje.id"
                    class="px-2 py-1 rounded"
                    :style="{ backgroundColor: getColor('primary-lighten-4'), color: 'white' }"
                  >
                    {{ '(' + viaje.embarcacionData.id_lancha + ') ' + viaje.embarcacionData.nombre_embarcacion }}
                  </div>
                </div>
              </template>
            </VaDataTable>
          </div>
          <VaButton
            v-if="
              showList.length > 0 &&
              (userRole!.role == UserRoles.ADMIN ||
                userRole!.role == UserRoles.SUPERVISOR ||
                userRole!.role == UserRoles.OPERADOR)
            "
            :disabled="selectedItemsEmitted.length == 0"
            class="mt-2"
            preset="danger"
            color="danger"
            @click="disableArmadoresSeleccionados"
          >
            Deshabilitar permisos de armadores seleccionados
          </VaButton>
        </div>
      </div>
    </VaCardContent>
  </VaCard>
</template>

<script setup lang="ts">
import { onMounted, ref, Ref, watch } from 'vue'
import { useColors, useModal, useToast, VaButton } from 'vuestic-ui'
import { IRequest, RequestObjectType, RequestStatus } from '../../../services/api-services/RequestsServices'
import dataStorageManager, { BucketDefaultIDs } from '../../../managers/data/DataStorageManager'
import { IArmador, OperatedArmador } from '../../../services/api-services/ArmadoresServices'
import { IUser, UserRoles } from '../../../services/api-services/UsersServices'
import { rutify, normalizeRut, rutMatches } from '../../../services/apiUtils'
import { ITripulante } from '../../../services/api-services/TripulantesServices'
import identityManager, { LoggedInUser } from '../../../managers/identity/IdentityManager'
import {
  defaultIIncidencia,
  IIncidencia,
  IncidenceCode,
  IncidenceType,
} from '../../../services/api-services/BitacoraServices'
import { IViaje } from '../../../services/api-services/ViajesServices'
import roleCapacitiesManager, { UserRole } from '../../../managers/identity/RoleCapacitiesManager'
import { IEmbarcacion } from '../../../services/api-services/EmbarcacionesServices'
import dataHubManager from '../../../managers/data/DataHubManager'
import { armadoresHubName } from '../../../services/data-hub-services/ArmadoresHub'

interface ViajeEmbarcacion extends IViaje {
  embarcacionData: IEmbarcacion
  armadorRequest: IRequest
}

interface ArmadorViajes extends OperatedArmador {
  activeViajes: ViajeEmbarcacion[]
}

const { init: notify } = useToast()
const { confirm } = useModal()

const { getColor } = useColors()

const userRole: Ref<UserRole | null> = roleCapacitiesManager.getRefUserRole()

const operatedArmadoresList: Ref<Array<OperatedArmador>> = ref([])
const fullArmadoresList: Ref<Array<ArmadorViajes>> = ref([])

const embarcacionesList: Ref<Array<IEmbarcacion>> = ref([])

const requestsList: Ref<IRequest[]> = ref([])
const changesRequests: Ref<IRequest[]> = ref([])
const asociationsRequests: Ref<IRequest[]> = ref([])

const preacreditacionRequests: Ref<IRequest[]> = ref([])
const posicionamientoRequests: Ref<IRequest[]> = ref([])
const viajesList: Ref<IViaje[]> = ref([])

const patronesRequests: Ref<IRequest[]> = ref([])
const patronesList: Ref<ITripulante[]> = ref([])

const selectedPeriod = ref('RUT') as Ref<'RUT' | 'Nombre'>
const periods = ['RUT', 'Nombre'].map((period) => ({ label: period, value: period }))
const searchQuery = ref('')
const showSearch = ref(true)
const selectedItemsEmitted: Ref<Array<ArmadorViajes>> = ref([])

const dataList: Ref<Array<ArmadorViajes>> = ref([])
const showList: Ref<Array<ArmadorViajes>> = ref([])

onMounted(() => {
  dataHubManager.subscribeStorage<OperatedArmador>(armadoresHubName, 'deshabilitarArmador', (data) => {
    operatedArmadoresList.value = data
    console.log('Received data from ArmadoresHub:', data)
    loadFullArmadoresList()
  })
  dataStorageManager.subscribeBucket<IViaje>(BucketDefaultIDs.VIAJES, (viajes) => {
    onViajesLoad(viajes)
  })
  dataStorageManager.subscribeBucket(BucketDefaultIDs.TRIPULANTES, (tripulantes: Array<ITripulante>) => {
    onTripulantesLoad(tripulantes)
  })

  dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, onRequestsLoad)
})

watch(fullArmadoresList, () => {
  dataList.value = fullArmadoresList.value
})

watch(dataList, () => {
  showList.value = dataList.value
})

watch(searchQuery, () => {
  if (searchQuery.value.trim() === '') {
    showList.value = dataList.value
  } else {
    const query = searchQuery.value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    if (selectedPeriod.value === 'RUT') {
      showList.value = dataList.value.filter((item) =>
        item.tipo_persona === 'juridica'
          ? rutMatches(item.rut_empresa, searchQuery.value)
          : rutMatches(item.rut_representante, searchQuery.value),
      )
    } else if (selectedPeriod.value === 'Nombre') {
      showList.value = dataList.value.filter((item) =>
        item.tipo_persona === 'juridica'
          ? item.razon_social.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().includes(query)
          : item.nombre_representante.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().includes(query),
      )
    }
  }
})

watch(selectedPeriod, () => {
  searchQuery.value = ''
  showList.value = dataList.value
})

function onRequestsLoad(requests: Array<IRequest>) {
  requestsList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Armador && r.tipo_objeto_2 === null)
  changesRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Modificacion)
  asociationsRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Asociacion_embarcacion)
  preacreditacionRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Preacreditacion)
  posicionamientoRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PosicionamientoViaje)
  patronesRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.PatronViaje)
  loadFullArmadoresList()
}

function loadFullArmadoresList() {
  fullArmadoresList.value = operatedArmadoresList.value
    .map((item) => {
      const armadorTripulante = dataStorageManager.searchDataInBucket<ITripulante>(
        BucketDefaultIDs.TRIPULANTES,
        (t) => rutify(t.rut) === rutify(item.rut_representante),
      )
      const preacreditacionesArmador = preacreditacionRequests.value.filter(
        (req) =>
          req.tipo_objeto_2 === RequestObjectType.Armador &&
          req.id_objeto_2 &&
          req.id_objeto_2.toString() === item.id.toString(),
      )
      const preacArmadorPatron = preacreditacionRequests.value.filter(
        (req) =>
          armadorTripulante &&
          req.tipo_objeto_2 === RequestObjectType.Tripulante &&
          req.id_objeto_2 &&
          req.id_objeto_2.toString() === armadorTripulante.id.toString(),
      )
      const activeViajes: Array<ViajeEmbarcacion> = viajesList.value
        .filter((v) => {
          const preArmPtr = preacArmadorPatron
            .filter((p) => p.id_objeto.toString() === v.id.toString())
            .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
          const preArmAmd = preacreditacionesArmador
            .filter((p) => p.id_objeto.toString() === v.id.toString())
            .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
          return (
            new Date(v.fecha_viaje).getTime() >= new Date().getTime() - 1000 * 60 * 60 * 10 &&
            new Date(v.fecha_viaje).getTime() <= new Date().getTime() + 1000 * 60 * 60 * 10 &&
            ((preArmAmd && preArmAmd.estado == RequestStatus.APPROVED) ||
              (preArmPtr && preArmPtr.estado == RequestStatus.APPROVED))
          )
        })
        .map((viaje) => {
          const armadorPatronRequest = preacArmadorPatron
            .filter((req) => req.id_objeto.toString() === viaje.id.toString())
            .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
          const armadorArmadorRequest = preacreditacionesArmador
            .filter((req) => req.id_objeto.toString() === viaje.id.toString())
            .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
          const embarcacionData = embarcacionesList.value.find(
            (e) => e.id.toString() === viaje.embarcacion_id.toString(),
          )
          if (embarcacionData) {
            return {
              ...viaje,
              embarcacionData: embarcacionData,
              armadorRequest: armadorPatronRequest ? armadorPatronRequest : armadorArmadorRequest,
            }
          } else {
            return undefined
          }
        })
        .filter((v) => v !== undefined) as Array<ViajeEmbarcacion>
      return {
        ...item,
        activeViajes: activeViajes,
      }
    })
    .filter(
      (e) =>
        e.activeViajes.length > 0 &&
        e.requestData &&
        (e.requestData.estado === RequestStatus.APPROVED || e.requestData.estado === RequestStatus.REJECTED),
    )
    .sort((a, b) => {
      const nameA = a.tipo_persona === 'juridica' ? a.razon_social.toUpperCase() : a.nombre_representante.toUpperCase()
      const nameB = b.tipo_persona === 'juridica' ? b.razon_social.toUpperCase() : b.nombre_representante.toUpperCase()
      if (nameA < nameB) {
        return -1
      }
      if (nameA > nameB) {
        return 1
      }
      return 0
    })
}

function onViajesLoad(viajes: Array<IViaje>) {
  viajesList.value = viajes
  loadFullArmadoresList()
}

function onTripulantesLoad(tripulantes: Array<ITripulante>) {
  patronesList.value = tripulantes
  loadFullArmadoresList()
}

const exportAsCSV = () => {}

async function disableArmadoresSeleccionados() {
  const confirmed = await confirm({
    title: 'Desactivar armador',
    message: `¿Estás seguro de desactivar los armadores seleccionados?`,
    okText: 'Sí, deshabilitar',
    cancelText: 'Cancelar',
  })
  if (confirmed) {
    const armadoresPromises = selectedItemsEmitted.value.map(async (armador) => {
      await onDesacreditar(armador)
    })
    await Promise.all(armadoresPromises)
    notify({
      message: `Armadores deshabilitados correctamente.`,
      color: 'success',
    })
  }
}

async function onDesacreditar(armador: ArmadorViajes) {
  const currentUser: LoggedInUser = identityManager.getUser()!

  const viajesPromises = armador.activeViajes.map(async (viajeAModificar) => {
    const revokedRequest: IRequest = {
      ...viajeAModificar.armadorRequest,
      revisor: currentUser.id_usuario,
      fecha_respuesta: new Date().toISOString(),
      estado: RequestStatus.REJECTED,
      fecha_caducidad: new Date().toISOString(),
      razon_rechazo: `Preacreditación revocada por deshabilitación de armador.`,
    }
    await dataStorageManager.performOperation(BucketDefaultIDs.SOLICITUDES, 'updateRequest', revokedRequest)
    const bitacoraData: IIncidencia = { ...defaultIIncidencia }
    bitacoraData.tipo_incidencia_id = IncidenceType.PREACREDITACION
    bitacoraData.codigo_incidencia = IncidenceCode.DESACREDITACION
    bitacoraData.descripcion = `Preacreditación desacreditada para viaje ID ${viajeAModificar.id} y tripulante ID ${viajeAModificar.armadorRequest.id_objeto_2} por deshabilitación de armador ID ${armador.id}.`
    bitacoraData.id_objeto = viajeAModificar.id
    await dataStorageManager.performOperation(BucketDefaultIDs.BITACORA, 'insertIncidencia', bitacoraData)
  })

  await Promise.all(viajesPromises)
}
</script>

<style lang="scss" scoped>
.region-revenue-table {
  ::v-deep(tbody) {
    tr {
      border-top: 1px solid var(--va-background-border);
    }
  }
}

.pers-content-container {
  max-height: 636px;
  height: 100%;
}

.scrollable-data-list {
  max-height: 520px;
  overflow-y: auto;
}

.region-revenue-table {
  min-width: 360px;
}

.body-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
}

.gapped-div {
  gap: 10px;
}

.padded-button {
  padding: 0 10px;
}
</style>
